import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Video, VideoOff, Mic, MicOff, PhoneOff, MonitorUp, MonitorX, Presentation, ChevronLeft, ChevronRight, X, Plus, MessageSquare, Send, Circle, Square, StickyNote, Download } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICE = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] };

/**
 * Group meeting dialog — WebRTC mesh (up to 4 recommended).
 * Modes: host (creates meeting) or joiner (joins given meetingId).
 */
export default function GroupCallDialog({ open, onOpenChange, meetingId: incomingMeetingId, participants = [], title = "Reunião rápida", audioOnly = false }) {
  const { user } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [remoteStreams, setRemoteStreams] = useState({}); // {peer_id: MediaStream}
  const localStreamRef = useRef(null);
  const cameraTrackRef = useRef(null);  // original camera track kept for restoring after screen share
  const screenStreamRef = useRef(null);
  const [screenSharing, setScreenSharing] = useState(false);
  const [slidesOpen, setSlidesOpen] = useState(false);
  const [slides, setSlides] = useState([]);   // [{url, name, note}]
  const [slideIdx, setSlideIdx] = useState(0);
  const [uploadingSlide, setUploadingSlide] = useState(false);
  // Chat
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatDraft, setChatDraft] = useState("");
  const chatSinceRef = useRef(null);
  const chatPollRef = useRef(null);
  const chatScrollRef = useRef(null);
  const [chatUnread, setChatUnread] = useState(0);
  // Recording
  const recorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const recordStartRef = useRef(null);
  const [recordSecs, setRecordSecs] = useState(0);
  const recordTimerRef = useRef(null);
  // Laser pointer + notes
  const [laserOn, setLaserOn] = useState(false);
  const [laserPos, setLaserPos] = useState(null); // {x%, y%} relative to slide area
  const [notesOpen, setNotesOpen] = useState(false);
  const pcsRef = useRef({}); // {peer_id: RTCPeerConnection}
  const pollRef = useRef(null);
  const stoppedRef = useRef(false);
  const declinedRef = useRef(new Set());
  const localVideoRef = useRef(null);

  const start = async () => {
    stoppedRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: !audioOnly });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
    } catch (e) {
      toast.error("Permita câmera e microfone para entrar na reunião");
      onOpenChange(false);
      return;
    }
    let mtg;
    if (incomingMeetingId) {
      const r = await axios.post(`${API}/meetings/${incomingMeetingId}/join`);
      mtg = r.data;
    } else {
      const r = await axios.post(`${API}/meetings/start`, { participants, title, audio_only: audioOnly, kind: participants.length ? "call" : "meeting" });
      mtg = r.data;
      toast.success(participants.length ? "Chamando..." : "Reunião criada — convites enviados");
    }
    setMeeting(mtg);
    // Start polling for meeting state + incoming signals
    pollRef.current = setInterval(() => tick(mtg.meeting_id), 2500);
    tick(mtg.meeting_id);
  };

  const stop = async () => {
    stoppedRef.current = true;
    clearInterval(pollRef.current);
    clearInterval(chatPollRef.current);
    clearInterval(recordTimerRef.current);
    try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); } catch {}
    setRecording(false);
    try {
      if (meeting) await axios.post(`${API}/meetings/${meeting.meeting_id}/leave`);
    } catch { /* silent */ }
    (localStreamRef.current?.getTracks() || []).forEach(t => t.stop());
    (screenStreamRef.current?.getTracks() || []).forEach(t => t.stop());
    screenStreamRef.current = null;
    cameraTrackRef.current = null;
    setScreenSharing(false);
    setSlidesOpen(false);
    setChatOpen(false);
    setChatMessages([]);
    setChatUnread(0);
    chatSinceRef.current = null;
    Object.values(pcsRef.current).forEach(pc => { try { pc.close(); } catch {} });
    pcsRef.current = {};
    setRemoteStreams({});
    setMeeting(null);
  };

  // Keyboard navigation for the slides overlay
  useEffect(() => {
    if (!slidesOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") setSlideIdx((i) => Math.min(slides.length - 1, i + 1));
      else if (e.key === "ArrowLeft") setSlideIdx((i) => Math.max(0, i - 1));
      else if (e.key === "Escape") closeSlides();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slidesOpen, slides.length]);

  useEffect(() => {
    if (open) start();
    return () => { if (open) stop(); /* eslint-disable-next-line */ };
    // eslint-disable-next-line
  }, [open]);

  const ensurePc = (peerId) => {
    if (pcsRef.current[peerId]) return pcsRef.current[peerId];
    const pc = new RTCPeerConnection(ICE);
    (localStreamRef.current?.getTracks() || []).forEach(t => pc.addTrack(t, localStreamRef.current));
    pc.ontrack = (ev) => {
      setRemoteStreams((prev) => ({ ...prev, [peerId]: ev.streams[0] }));
    };
    pc.onicecandidate = (ev) => {
      if (ev.candidate && meeting) {
        axios.post(`${API}/meetings/${meeting.meeting_id}/signal`, {
          kind: "ice", to_user_id: peerId, payload: { candidate: ev.candidate.toJSON() },
        }).catch(() => {});
      }
    };
    pcsRef.current[peerId] = pc;
    return pc;
  };

  const tick = async (mid) => {
    if (stoppedRef.current) return;
    try {
      const [state, inbox] = await Promise.all([
        axios.get(`${API}/meetings/${mid}`),
        axios.get(`${API}/meetings/${mid}/signals/inbox`),
      ]);
      const m = state.data;
      setMeeting(m);
      for (const d of (m.declined || [])) {
        if (!declinedRef.current.has(d)) {
          declinedRef.current.add(d);
          toast.error(`${m.peers?.[d]?.name || "Participante"} recusou a chamada`);
          if (!incomingMeetingId && (m.invited || []).length && (m.invited || []).every(p => (m.declined || []).includes(p))) { toast.info("Chamada recusada — encerrando"); setTimeout(() => onOpenChange(false), 800); }
        }
      }
      // For every other joined peer we don't have PC yet, initiate offer if my id < peer id (deterministic offerer)
      const joined = (m.joined || []).filter(uid => uid !== user.user_id);
      for (const peerId of joined) {
        if (!pcsRef.current[peerId] && user.user_id < peerId) {
          const pc = ensurePc(peerId);
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          await axios.post(`${API}/meetings/${mid}/signal`, {
            kind: "offer", to_user_id: peerId, payload: { sdp: pc.localDescription },
          });
        }
      }
      // Process incoming signals
      for (const sig of (inbox.data || [])) {
        const pc = ensurePc(sig.from_user_id);
        if (sig.kind === "offer") {
          await pc.setRemoteDescription(new RTCSessionDescription(sig.payload.sdp));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          await axios.post(`${API}/meetings/${mid}/signal`, {
            kind: "answer", to_user_id: sig.from_user_id, payload: { sdp: pc.localDescription },
          });
        } else if (sig.kind === "answer") {
          if (!pc.currentRemoteDescription) {
            await pc.setRemoteDescription(new RTCSessionDescription(sig.payload.sdp));
          }
        } else if (sig.kind === "ice") {
          try { await pc.addIceCandidate(new RTCIceCandidate(sig.payload.candidate)); } catch { /* ignore */ }
        }
      }
      // Close PCs for peers who left
      Object.keys(pcsRef.current).forEach(pid => {
        if (!m.joined?.includes(pid)) {
          try { pcsRef.current[pid].close(); } catch {}
          delete pcsRef.current[pid];
          setRemoteStreams(prev => { const n = { ...prev }; delete n[pid]; return n; });
        }
      });
      if (m.status === "ended") {
        toast.info("Reunião encerrada");
        onOpenChange(false);
      }
    } catch { /* silent */ }
  };

  const toggleMute = () => {
    const audio = (localStreamRef.current?.getAudioTracks() || [])[0];
    if (audio) { audio.enabled = !audio.enabled; setMuted(!audio.enabled); }
  };
  const toggleVideo = () => {
    const video = (localStreamRef.current?.getVideoTracks() || [])[0];
    if (video) { video.enabled = !video.enabled; setVideoOff(!video.enabled); }
  };

  // ============ SCREEN SHARE ============
  const replaceVideoTrackForAll = (newTrack) => {
    Object.values(pcsRef.current).forEach((pc) => {
      const sender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
      if (sender) { try { sender.replaceTrack(newTrack); } catch { /* ignore */ } }
    });
  };

  const startScreenShare = async () => {
    if (screenSharing) return;
    try {
      const screen = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      screenStreamRef.current = screen;
      const screenTrack = screen.getVideoTracks()[0];
      // Remember the original camera track
      if (!cameraTrackRef.current) {
        cameraTrackRef.current = (localStreamRef.current?.getVideoTracks() || [])[0] || null;
      }
      replaceVideoTrackForAll(screenTrack);
      if (localVideoRef.current) localVideoRef.current.srcObject = screen;
      screenTrack.onended = () => stopScreenShare();
      setScreenSharing(true);
      toast.success("Compartilhando sua tela");
    } catch {
      toast.error("Não foi possível compartilhar a tela");
    }
  };

  const stopScreenShare = () => {
    if (!screenSharing && !screenStreamRef.current) return;
    (screenStreamRef.current?.getTracks() || []).forEach((t) => t.stop());
    screenStreamRef.current = null;
    const cam = cameraTrackRef.current;
    if (cam) {
      replaceVideoTrackForAll(cam);
      if (localVideoRef.current && localStreamRef.current) localVideoRef.current.srcObject = localStreamRef.current;
    }
    setScreenSharing(false);
    setSlidesOpen(false);
    toast.info("Compartilhamento de tela encerrado");
  };

  // ============ SLIDES ============
  const uploadSlide = async (file) => {
    if (!file) return;
    setUploadingSlide(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const raw = r.data?.url || r.data?.file_url || r.data?.path;
      // Store relative path so URL stays valid across preview/production domains.
      const url = raw?.startsWith("http") ? raw : raw;
      setSlides((s) => [...s, { url, name: file.name }]);
      toast.success("Slide adicionado");
    } catch {
      toast.error("Erro no upload do slide");
    }
    setUploadingSlide(false);
  };

  const startSlides = async () => {
    if (slides.length === 0) {
      toast.error("Envie pelo menos um slide antes de apresentar");
      return;
    }
    setSlidesOpen(true);
    setSlideIdx(0);
    // Ensure screen share is active so viewers see the slides window
    if (!screenSharing) await startScreenShare();
  };

  const closeSlides = () => {
    setSlidesOpen(false);
    if (screenSharing) stopScreenShare();
  };

  const removeSlide = (i) => {
    setSlides((s) => s.filter((_, idx) => idx !== i));
    setSlideIdx((cur) => Math.max(0, Math.min(cur, slides.length - 2)));
  };

  const setSlideNote = (i, note) => {
    setSlides((s) => s.map((sl, idx) => (idx === i ? { ...sl, note } : sl)));
  };

  // ============ CHAT ============
  useEffect(() => {
    if (!meeting) return undefined;
    const load = async () => {
      try {
        const params = chatSinceRef.current ? `?since=${encodeURIComponent(chatSinceRef.current)}` : "";
        const r = await axios.get(`${API}/meetings/${meeting.meeting_id}/chat${params}`);
        if (r.data.length) {
          chatSinceRef.current = r.data[r.data.length - 1].created_at;
          setChatMessages((prev) => {
            const seen = new Set(prev.map((m) => m.message_id));
            const merged = [...prev];
            for (const m of r.data) if (!seen.has(m.message_id)) merged.push(m);
            return merged;
          });
          if (!chatOpen) setChatUnread((n) => n + r.data.filter((m) => m.from_user_id !== user.user_id).length);
        }
      } catch { /* silent */ }
    };
    load();
    chatPollRef.current = setInterval(load, 2000);
    return () => clearInterval(chatPollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting?.meeting_id]);

  useEffect(() => {
    if (chatOpen) {
      setChatUnread(0);
      // scroll to bottom
      setTimeout(() => {
        if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
      }, 50);
    }
  }, [chatOpen, chatMessages.length]);

  const sendChat = async () => {
    const text = chatDraft.trim();
    if (!text || !meeting) return;
    setChatDraft("");
    try {
      await axios.post(`${API}/meetings/${meeting.meeting_id}/chat`, { text });
      // Optimistic add
      setChatMessages((prev) => [...prev, {
        message_id: "local_" + Date.now(),
        from_user_id: user.user_id,
        from_name: user.name || (user.email || "").split("@")[0],
        from_picture: user.picture,
        text,
        created_at: new Date().toISOString(),
      }]);
    } catch { toast.error("Erro ao enviar"); }
  };

  // ============ RECORDING ============
  const buildRecordingStream = () => {
    // Compose a stream that mixes the local outgoing video (camera or screen)
    // with the local audio track. This is what viewers "see" from the host.
    const chunks = [];
    const video = (screenSharing && screenStreamRef.current)
      ? screenStreamRef.current.getVideoTracks()[0]
      : (localStreamRef.current?.getVideoTracks() || [])[0];
    const audio = (localStreamRef.current?.getAudioTracks() || [])[0];
    if (!video) return null;
    return new MediaStream([video, audio].filter(Boolean).map((t) => t)); // eslint-disable-line no-return-assign
  };

  const startRecording = () => {
    if (recording) return;
    const stream = buildRecordingStream();
    if (!stream) { toast.error("Sem vídeo para gravar"); return; }
    try {
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus"
        : (MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm");
      const rec = new MediaRecorder(stream, { mimeType: mime });
      recordChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) recordChunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(recordChunksRef.current, { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        a.download = `reuniao-${stamp}.webm`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast.success("Gravação salva no seu dispositivo");
      };
      rec.start(1000); // one chunk per second
      recorderRef.current = rec;
      recordStartRef.current = Date.now();
      setRecordSecs(0);
      recordTimerRef.current = setInterval(() => {
        setRecordSecs(Math.floor((Date.now() - recordStartRef.current) / 1000));
      }, 1000);
      setRecording(true);
      toast.success("Gravação iniciada");
    } catch {
      toast.error("Seu navegador não suporta gravação");
    }
  };

  const stopRecording = () => {
    if (!recording) return;
    try { recorderRef.current?.stop(); } catch {}
    clearInterval(recordTimerRef.current);
    setRecording(false);
  };

  const peers = meeting ? (meeting.joined || []).filter(uid => uid !== user.user_id) : [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) stop(); onOpenChange(v); }}>
      <DialogContent className="max-w-5xl p-0 bg-black overflow-hidden" data-testid="group-call-dialog">
        <div className="p-3 bg-gradient-to-r from-primary/80 to-purple-600 text-white flex items-center justify-between">
          <div>
            <div className="text-sm font-bold">{meeting?.title || title}</div>
            <div className="text-[10px] opacity-80">
              {meeting?.status === "active" ? `${(meeting?.joined || []).length} participante(s)` : "conectando..."}
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs" data-testid="group-call-id">
            {recording && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600/90 font-semibold animate-pulse" data-testid="record-indicator">
                <Circle className="w-2 h-2" fill="currentColor" /> REC {Math.floor(recordSecs/60)}:{String(recordSecs%60).padStart(2,"0")}
              </span>
            )}
            ID: <span className="font-mono">{meeting?.meeting_id?.slice(-8) || "..."}</span>
          </div>
        </div>
        <div className="flex">
          <div className="flex-1 min-w-0">
            <div className={`grid gap-1 p-1 bg-black ${peers.length > 1 ? "grid-cols-2" : "grid-cols-1"}`} style={{ minHeight: 360 }}>
              {/* Local */}
              <div className="relative bg-neutral-900 rounded aspect-video overflow-hidden">
                <video ref={localVideoRef} autoPlay muted playsInline className="w-full h-full object-cover" data-testid="local-video" />
                <div className="absolute bottom-1 left-1 text-white text-[10px] bg-black/60 rounded px-2 py-0.5">{user?.name} (você) {muted && "🔇"}</div>
              </div>
              {/* Remotes */}
              {peers.map(pid => {
                const info = meeting?.peers?.[pid] || {};
                const stream = remoteStreams[pid];
                return (
                  <RemoteVideoTile
                    key={pid}
                    peerId={pid}
                    info={info}
                    stream={stream}
                  />
                );
              })}
            </div>
          </div>
          {chatOpen && (
            <div className="w-72 border-l border-neutral-800 bg-neutral-950 flex flex-col" data-testid="meeting-chat-panel" style={{ minHeight: 362 }}>
              <div className="p-2 flex items-center justify-between text-white text-xs font-semibold border-b border-neutral-800">
                <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Chat da reunião</span>
                <button onClick={() => setChatOpen(false)} data-testid="btn-chat-close"><X className="w-4 h-4" /></button>
              </div>
              <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-2 space-y-2" data-testid="chat-messages">
                {chatMessages.length === 0 && (
                  <p className="text-neutral-500 text-xs text-center pt-4">Sem mensagens ainda. Escreva a primeira!</p>
                )}
                {chatMessages.map((m) => {
                  const mine = m.from_user_id === user.user_id;
                  return (
                    <div key={m.message_id} className={`text-xs ${mine ? "text-right" : ""}`} data-testid={`chat-msg-${m.message_id}`}>
                      {!mine && <div className="text-[10px] text-neutral-400 mb-0.5">{m.from_name}</div>}
                      <div className={`inline-block max-w-[85%] px-2 py-1.5 rounded-lg break-words ${mine ? "bg-primary text-primary-foreground" : "bg-neutral-800 text-white"}`}>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-2 border-t border-neutral-800 flex gap-1">
                <Input
                  value={chatDraft}
                  onChange={(e) => setChatDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                  placeholder="Digite uma mensagem..."
                  className="h-8 text-xs bg-neutral-900 text-white border-neutral-700"
                  data-testid="chat-input"
                />
                <Button size="sm" onClick={sendChat} disabled={!chatDraft.trim()} data-testid="btn-chat-send">
                  <Send className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="p-3 bg-black flex justify-center gap-2 flex-wrap">
          <Button size="sm" variant="secondary" data-testid="btn-toggle-mute" onClick={toggleMute}>
            {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="secondary" data-testid="btn-toggle-video" onClick={toggleVideo}>
            {videoOff ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />}
          </Button>
          <Button
            size="sm"
            variant={screenSharing ? "default" : "secondary"}
            data-testid="btn-screen-share"
            onClick={() => (screenSharing ? stopScreenShare() : startScreenShare())}
            title={screenSharing ? "Parar compartilhamento" : "Compartilhar tela"}
          >
            {screenSharing ? <MonitorX className="w-4 h-4" /> : <MonitorUp className="w-4 h-4" />}
            <span className="ml-1 hidden sm:inline">{screenSharing ? "Parar tela" : "Tela"}</span>
          </Button>
          <Button
            size="sm"
            variant={slidesOpen ? "default" : "secondary"}
            data-testid="btn-slides"
            onClick={() => (slidesOpen ? closeSlides() : startSlides())}
            title="Apresentar slides"
          >
            <Presentation className="w-4 h-4" />
            <span className="ml-1 hidden sm:inline">Slides</span>
          </Button>
          <Button
            size="sm"
            variant="secondary"
            data-testid="btn-chat-toggle"
            onClick={() => setChatOpen((v) => !v)}
            title="Chat da reunião"
            className="relative"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="ml-1 hidden sm:inline">Futuro Chat</span>
            {chatUnread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-red-500 text-white flex items-center justify-center">
                {chatUnread}
              </span>
            )}
          </Button>
          <Button
            size="sm"
            variant={recording ? "destructive" : "secondary"}
            data-testid="btn-record"
            onClick={() => (recording ? stopRecording() : startRecording())}
            title={recording ? "Parar gravação" : "Gravar reunião"}
          >
            {recording ? <Square className="w-4 h-4" fill="currentColor" /> : <Circle className="w-4 h-4" fill="currentColor" />}
            <span className="ml-1 hidden sm:inline">
              {recording ? `REC ${Math.floor(recordSecs/60)}:${String(recordSecs%60).padStart(2,"0")}` : "Gravar"}
            </span>
          </Button>
          <Button size="sm" variant="destructive" data-testid="btn-hangup" onClick={() => { stop(); onOpenChange(false); }}>
            <PhoneOff className="w-4 h-4" />
          </Button>
        </div>

        {/* Slides upload strip (visible whenever there's at least one slide OR presenter is preparing) */}
        {(slides.length > 0 || slidesOpen) && (
          <div className="p-2 bg-neutral-900 border-t border-neutral-800 flex items-center gap-2 overflow-x-auto" data-testid="slides-strip">
            <label className="inline-flex items-center gap-1 text-xs text-white cursor-pointer px-2 py-1.5 rounded border border-white/20 hover:bg-white/10 whitespace-nowrap">
              <Plus className="w-3 h-3" /> {uploadingSlide ? "Enviando..." : "Adicionar slide"}
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => uploadSlide(e.target.files?.[0])}
                data-testid="slide-upload-input" disabled={uploadingSlide} />
            </label>
            {slides.map((s, i) => (
              <div key={i} className={`relative rounded overflow-hidden border-2 flex-shrink-0 cursor-pointer ${i === slideIdx && slidesOpen ? "border-primary" : "border-transparent"}`}
                onClick={() => setSlideIdx(i)} data-testid={`slide-thumb-${i}`}>
                <img src={s.url} alt={s.name} className="w-20 h-12 object-cover bg-black" />
                <button className="absolute top-0 right-0 bg-black/60 text-white p-0.5 rounded-bl"
                  onClick={(e) => { e.stopPropagation(); removeSlide(i); }} data-testid={`slide-remove-${i}`}>
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>

      {/* Slides fullscreen presenter overlay — the presenter's screen is being shared to peers */}
      {slidesOpen && slides.length > 0 && (
        <div className="fixed inset-0 z-[70] bg-black flex items-center justify-center" data-testid="slides-presenter">
          <div
            className="relative max-w-full max-h-full"
            onMouseMove={(e) => {
              if (!laserOn) return;
              const rect = e.currentTarget.getBoundingClientRect();
              setLaserPos({ x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 });
            }}
            onMouseLeave={() => setLaserPos(null)}
          >
            <img src={slides[slideIdx]?.url} alt={`slide ${slideIdx + 1}`} className="max-w-full max-h-full object-contain" />
            {laserOn && laserPos && (
              <div
                className="pointer-events-none absolute"
                style={{ left: `${laserPos.x}%`, top: `${laserPos.y}%`, transform: "translate(-50%, -50%)" }}
                data-testid="laser-pointer"
              >
                <div className="w-4 h-4 rounded-full bg-red-500 shadow-[0_0_16px_4px_rgba(239,68,68,0.9)] animate-pulse" />
              </div>
            )}
          </div>
          <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-white pointer-events-none">
            <span className="px-2 py-1 rounded-full bg-white/10 text-xs font-semibold pointer-events-auto">
              Apresentando · slide {slideIdx + 1} / {slides.length}
            </span>
            <div className="flex gap-2 pointer-events-auto">
              <Button
                size="sm"
                variant={laserOn ? "default" : "secondary"}
                onClick={() => setLaserOn((v) => !v)}
                data-testid="btn-laser-toggle"
                title="Ponteiro laser"
              >
                <Circle className="w-4 h-4" fill={laserOn ? "currentColor" : "none"} />
                <span className="ml-1 hidden sm:inline">Laser</span>
              </Button>
              <Button
                size="sm"
                variant={notesOpen ? "default" : "secondary"}
                onClick={() => setNotesOpen((v) => !v)}
                data-testid="btn-notes-toggle"
                title="Notas do apresentador (só você vê)"
              >
                <StickyNote className="w-4 h-4" />
                <span className="ml-1 hidden sm:inline">Notas</span>
              </Button>
              <Button size="sm" variant="secondary" onClick={closeSlides} data-testid="btn-close-slides">
                <X className="w-4 h-4 mr-1" /> Sair
              </Button>
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-8 flex items-center justify-center gap-3 text-white">
            <Button size="lg" variant="secondary" onClick={() => setSlideIdx(Math.max(0, slideIdx - 1))} disabled={slideIdx === 0} data-testid="btn-slide-prev">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="text-sm font-mono">{slideIdx + 1} / {slides.length}</span>
            <Button size="lg" variant="secondary" onClick={() => setSlideIdx(Math.min(slides.length - 1, slideIdx + 1))} disabled={slideIdx >= slides.length - 1} data-testid="btn-slide-next">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          {/* Presenter-only notes panel (bottom-left). Only visible on the presenter's screen.
              Tip: Use "Compartilhar janela" for perfect privacy — with tab share this may still be captured. */}
          {notesOpen && (
            <div className="absolute left-3 bottom-3 w-80 max-w-[45vw] bg-neutral-900/95 border border-neutral-700 rounded-lg p-2 shadow-2xl text-white" data-testid="slide-notes-panel">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold flex items-center gap-1"><StickyNote className="w-3.5 h-3.5" /> Notas do slide {slideIdx + 1} (só você vê)</span>
                <button onClick={() => setNotesOpen(false)}><X className="w-3.5 h-3.5" /></button>
              </div>
              <Textarea
                value={slides[slideIdx]?.note || ""}
                onChange={(e) => setSlideNote(slideIdx, e.target.value)}
                placeholder="Anotações privadas — dicas de fala, números-chave, próximas etapas..."
                rows={4}
                className="bg-neutral-800 text-white text-xs border-neutral-700"
                data-testid="slide-notes-input"
              />
              <p className="text-[10px] text-neutral-400 mt-1">Compartilhe apenas a janela do slide para que estas notas fiquem invisíveis para os participantes.</p>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** Isolated video tile — uses useRef + useEffect to sync srcObject without remounting <video>. */
function RemoteVideoTile({ peerId, info, stream }) {
  const videoRef = useRef(null);
  useEffect(() => {
    const el = videoRef.current;
    if (el && stream && el.srcObject !== stream) {
      el.srcObject = stream;
    }
  }, [stream]);
  return (
    <div className="relative bg-neutral-900 rounded aspect-video overflow-hidden" data-testid={`remote-video-${peerId}`}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className="w-full h-full object-cover"
        style={{ display: stream ? "block" : "none" }}
      />
      {!stream && (
        <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center text-white gap-2">
          <Avatar className="w-16 h-16"><AvatarImage src={info.picture} /><AvatarFallback>{info.name?.[0]}</AvatarFallback></Avatar>
          <div className="text-xs">Conectando com {info.name}...</div>
        </div>
      )}
      <div className="absolute bottom-1 left-1 text-white text-[10px] bg-black/60 rounded px-2 py-0.5">{info.name}</div>
    </div>
  );
}
